import { useEffect, useRef, useState } from "preact/hooks";
import { api } from "../api";
import { playAcknowledgement } from "../audio";
import { noteTilt } from "../../shared/notes";
import { DogEarSheet, SLIP_HINT_ID } from "./DogEar";
import {
  canEditSpeedrun,
  SPEEDRUN_BACK_LABEL,
  SPEEDRUN_EDIT_LABEL,
  SPEEDRUN_HEAD,
  SPEEDRUN_LINK_LABEL,
  SPEEDRUN_NOTHING_YET,
  SPEEDRUN_SAVE_BUSY,
  SPEEDRUN_SAVE_LABEL,
  SPEEDRUN_SHEET_LABEL,
  SPEEDRUN_TIME_LABEL,
  SPEEDRUN_TIME_PLACEHOLDER,
  SPEEDRUN_TITLE_LABEL,
  SPEEDRUN_TITLE_MAX,
  SPEEDRUN_TITLE_PLACEHOLDER,
  speedrunFormError,
  speedrunHideLabel,
  speedrunMetaLine,
  speedrunSlipLabel,
  speedrunWatchPixels,
  SPEEDRUN_WATCH_SIZE,
  SPEEDRUN_TIME_COLOUR,
  type SpeedrunNote,
  type SpeedrunState,
} from "../../shared/speedrun";
import type { Role } from "../../shared/types";
import { PixelIcon } from "./PixelArt";
import { BusyLabel } from "./BusyLabel";
import { ComposerSheet, holdKeyboard } from "./ComposerSheet";
import { ErrorBox } from "./Shell";

/** The speedrun slip's fixed id: its tilt (shared/notes.ts noteTilt) and the board's one-open state. */
export const SPEEDRUN_SLIP_ID = "speedrun";

const WATCH = speedrunWatchPixels();
const { w: WATCH_W, h: WATCH_H } = SPEEDRUN_WATCH_SIZE;

/** The time in its muted pill (shared/speedrun.ts SPEEDRUN_TIME_COLOUR): a name tag's shape, no peon's colour. */
const CLOCK_STYLE = { "--clock-tint": SPEEDRUN_TIME_COLOUR.tint, "--clock-swatch": SPEEDRUN_TIME_COLOUR.swatch, "--clock-ink": SPEEDRUN_TIME_COLOUR.ink };

function Clock({ time }: { time: string }) {
  return (
    <span class="speedrun-clock" style={CLOCK_STYLE}>
      {time}
    </span>
  );
}

/**
 * The stopwatch stamp (shared/speedrun.ts SPEEDRUN_WATCH): the peon stamp's frame (`.peon-stamp`, the
 * same box a note's author stamp has) around the stopwatch drawn on the small peon's grid, 46x42 art
 * pixels at two thirds of a CSS pixel each, in rects.
 */
export function SpeedrunWatch() {
  return (
    <span class="peon-stamp speedrun-watch" aria-hidden="true">
      <svg width={WATCH_W * SPEEDRUN_WATCH_SIZE.scale} height={WATCH_H * SPEEDRUN_WATCH_SIZE.scale} viewBox={`0 0 ${WATCH_W} ${WATCH_H}`} shape-rendering="crispEdges" aria-hidden="true" focusable="false">
        {WATCH.map((p) => (
          <rect key={`${p.x}-${p.y}`} x={p.x} y={p.y} width={p.w} height="1" fill={p.fill} />
        ))}
      </svg>
    </span>
  );
}

/**
 * The speedrun note on the notice board (docs/notes.md "The speedrun note", flag speedrunUI): a slip
 * like any note's, first on the cork, that never comes down. Closed: the stopwatch where a note's
 * peon stamp sits, the time and the game. Open, in place like a note: whose best it is, the game,
 * the category, the time, the place and the day, "See the run" out to speedrun.com, and "Change"
 * for Hue and stewards. No hearts, no Remove, no expiry.
 */
export function SpeedrunSlip({ note, open, onToggle, me, role, onSaved }: { note: SpeedrunNote; open: boolean; onToggle: () => void; me: string; role: Role; onSaved: (note: SpeedrunNote | null) => void }) {
  const mayEdit = canEditSpeedrun({ name: me, role });
  const [editing, setEditing] = useState(false);
  const meta = speedrunMetaLine(note);
  const style = open ? undefined : { transform: `rotate(${noteTilt(SPEEDRUN_SLIP_ID)}deg)` };
  return (
    <div class={`slip slip-speedrun${open ? " open" : " dog-ear"}`} style={style}>
      {!open && <DogEarSheet />}
      <PixelIcon name="pinned" className="slip-pin" />
      <button type="button" class={open ? "slip-paper" : "slip-paper closed"} aria-expanded={open} aria-label={open ? undefined : speedrunSlipLabel(note)} onClick={onToggle} aria-describedby={open ? undefined : SLIP_HINT_ID}>
        {open ? (
          <>
            <span class="slip-head">
              <strong>{SPEEDRUN_HEAD}</strong>
            </span>
            <span class="speedrun-open">
              <SpeedrunWatch />
              <span class="speedrun-what">
                <strong class="speedrun-title">{note.title}</strong>
                {note.category && <span class="speedrun-category">{note.category}</span>}
              </span>
            </span>
            <span class="speedrun-time">
              <Clock time={note.time} />
            </span>
            {meta && <span class="slip-meta speedrun-meta">{meta}</span>}
          </>
        ) : (
          <>
            <SpeedrunWatch />
            <span class="slip-text speedrun-closed">
              <strong class="speedrun-closed-time">
                <Clock time={note.time} />
              </strong>
              <span class="speedrun-closed-title">{note.title}</span>
            </span>
          </>
        )}
      </button>
      {open && (note.link || mayEdit) && (
        <div class="slip-actions" onClick={(e) => e.target === e.currentTarget && onToggle()}>
          {note.link && (
            <a class="btn-quiet speedrun-link" href={note.link} target="_blank" rel="noopener noreferrer">
              {SPEEDRUN_LINK_LABEL}
            </a>
          )}
          {mayEdit && (
            <button
              type="button"
              class="btn-quiet"
              onClick={() => {
                holdKeyboard();
                setEditing(true);
              }}
            >
              {SPEEDRUN_EDIT_LABEL}
            </button>
          )}
        </div>
      )}
      {editing && <SpeedrunSheet onClosed={() => setEditing(false)} onSaved={onSaved} />}
    </div>
  );
}

/**
 * The speedrun note's edit sheet (Hue's "Change" on the open slip, and Dev tools' "Change the
 * speedrun note"): a title and a time set by hand, which show in place of the live PB until "Back to
 * live". It opens filled with what shows now. From Dev tools (`devTools`) it also has "Take it down"
 * and "Put it back up". `onSaved` gets what the board shows after a write.
 */
export function SpeedrunSheet({ onClosed, onSaved, devTools = false }: { onClosed: () => void; onSaved?: (note: SpeedrunNote | null) => void; devTools?: boolean }) {
  const [state, setState] = useState<SpeedrunState | null>(null);
  const [title, setTitle] = useState("");
  const [time, setTime] = useState("");
  const [busy, setBusy] = useState<null | "save" | "live" | "hide">(null);
  const [error, setError] = useState<string | null>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const timeRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    let live = true;
    api.speedrun.get().then(
      (s) => {
        if (!live) return;
        setState(s);
        const from = s.set ?? (s.live ? { title: s.live.title, time: s.live.time } : null);
        if (from) {
          setTitle(from.title);
          setTime(from.time);
        }
      },
      (e) => live && setError(e instanceof Error ? e.message : "Could not load the note"),
    );
    return () => {
      live = false;
    };
  }, []);

  async function run(kind: "save" | "live" | "hide", close: (done?: boolean) => void) {
    if (busy) return;
    if (kind === "save") {
      const bad = speedrunFormError(title, time);
      if (bad) {
        setError(bad);
        (title.trim() ? timeRef : titleRef).current?.focus();
        return;
      }
    }
    setBusy(kind);
    setError(null);
    try {
      if (kind === "hide") {
        const { hidden } = await api.speedrun.hide(!state?.hidden);
        setState((s) => (s ? { ...s, hidden } : s));
        onSaved?.(hidden ? null : (state?.note ?? null));
        return;
      }
      const next = kind === "save" ? await api.speedrun.set(title, time) : await api.speedrun.live();
      void playAcknowledgement();
      onSaved?.(next.note);
      close(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save the note");
    } finally {
      setBusy(null);
    }
  }

  return (
    <ComposerSheet label={SPEEDRUN_SHEET_LABEL} onClosed={() => onClosed()}>
      {(close) => (
        <div class="note-composer speedrun-form">
          <h3 class="sheet-section-title">{SPEEDRUN_SHEET_LABEL}</h3>
          {state && !state.set && !state.live && <p class="muted small">{SPEEDRUN_NOTHING_YET}</p>}
          <label class="field-label" for="speedrun-title">
            {SPEEDRUN_TITLE_LABEL}
          </label>
          <input id="speedrun-title" ref={titleRef} class="input" type="text" maxLength={SPEEDRUN_TITLE_MAX} placeholder={SPEEDRUN_TITLE_PLACEHOLDER} value={title} onInput={(e) => setTitle(e.currentTarget.value)} autoFocus data-autofocus />
          <label class="field-label" for="speedrun-time">
            {SPEEDRUN_TIME_LABEL}
          </label>
          <input id="speedrun-time" ref={timeRef} class="input" type="text" inputMode="text" autoComplete="off" spellcheck={false} placeholder={SPEEDRUN_TIME_PLACEHOLDER} value={time} onInput={(e) => setTime(e.currentTarget.value)} onKeyDown={(e) => e.key === "Enter" && void run("save", close)} />
          {error && <ErrorBox message={error} />}
          <div class="note-composer-foot speedrun-foot">
            <span />
            <div class="note-composer-actions">
              <button type="button" class="btn-quiet" onClick={() => close()}>
                Cancel
              </button>
              <button type="button" class="btn btn-primary" onClick={() => void run("save", close)} disabled={busy !== null}>
                <BusyLabel busy={busy === "save"} label={SPEEDRUN_SAVE_BUSY}>{SPEEDRUN_SAVE_LABEL}</BusyLabel>
              </button>
            </div>
          </div>
          {(state?.set || (devTools && state)) && (
            <div class="speedrun-more">
              {state?.set && (
                <button type="button" class="btn-quiet" onClick={() => void run("live", close)} disabled={busy !== null}>
                  <BusyLabel busy={busy === "live"} label={SPEEDRUN_SAVE_BUSY}>{SPEEDRUN_BACK_LABEL}</BusyLabel>
                </button>
              )}
              {devTools && state && (
                <button type="button" class="btn-quiet" onClick={() => void run("hide", close)} disabled={busy !== null}>
                  {speedrunHideLabel(state.hidden)}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </ComposerSheet>
  );
}
